import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { questions, answerOptions } from "@/mocks/questions";
import { calculateDoshas, readProgress } from "@/lib/dosha";
import { assetUrl } from "@/lib/assetUrl";

interface Answers {
  [key: number]: number;
}

type Screen = "intro" | "quiz" | "processing";

export default function Teste() {
  const navigate = useNavigate();
  const [screen, setScreen] = useState<Screen>("intro");
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [direction, setDirection] = useState<"next" | "prev">("next");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [processingStep, setProcessingStep] = useState(0);
  const [exit, setExit] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Load saved progress
  useEffect(() => {
    fetch("api/progress", { credentials: "include" }).then(async response => {
      if (!response.ok) return;
      const data = await response.json();
      if (!data.progress) return;
      try {
        const parsed = readProgress(JSON.stringify({
          answers: JSON.parse(data.progress.answers_json || "{}"),
          currentQuestion: Number(data.progress.current_question || 0),
        }));
        if (!parsed) { localStorage.removeItem("dosha_test_progress"); return; }
        setAnswers(parsed.answers || {});
        if (Object.keys(parsed.answers).length > 0) {
          setCurrentQuestion(parsed.currentQuestion);
          setScreen("quiz");
        }
      } catch {
        // A cópia local continua disponível como recuperação.
      }
    }).catch(() => {});
  }, []);

  // Save progress
  useEffect(() => {
    if (screen === "quiz") {
      localStorage.setItem(
        "dosha_test_progress",
        JSON.stringify({ answers, currentQuestion })
      );
      const timer = window.setTimeout(() => {
        fetch("api/progress", {
          method: "PUT", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ answers, currentQuestion }),
        }).catch(() => {});
      }, 350);
      return () => window.clearTimeout(timer);
    }
  }, [answers, currentQuestion, screen]);

  // Processing animation
  useEffect(() => {
    if (screen === "processing") {
      const timers = [
        setTimeout(() => setProcessingStep(1), 800),
        setTimeout(() => setProcessingStep(2), 1600),
        setTimeout(() => setProcessingStep(3), 2400),
        setTimeout(() => setProcessingStep(4), 3200),
        setTimeout(() => setProcessingStep(5), 4000),
        setTimeout(() => {
          const result = calculateDoshas(answers);
          localStorage.setItem("dosha_test_result", JSON.stringify(result));
          localStorage.removeItem("dosha_test_progress");
          fetch("api/progress", {
            method: "PUT", credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ answers, currentQuestion: questions.length - 1, completed: true }),
          }).catch(() => {});
          setExit(true);
          setTimeout(() => navigate("/resultado-bloqueado"), 600);
        }, 4800),
      ];
      return () => timers.forEach(clearTimeout);
    }
  }, [screen, answers, navigate]);

  const handleAnswer = useCallback(
    (value: number) => {
      if (isTransitioning) return;
      const newAnswers = { ...answers, [questions[currentQuestion].id]: value };
      setAnswers(newAnswers);
      setIsTransitioning(true);
      setDirection("next");

      setTimeout(() => {
        if (currentQuestion < questions.length - 1) {
          setCurrentQuestion((prev) => prev + 1);
          setIsTransitioning(false);
        } else {
          setScreen("processing");
        }
      }, 400);
    },
    [currentQuestion, answers, isTransitioning]
  );

  const handleBack = useCallback(() => {
    if (currentQuestion > 0 && !isTransitioning) {
      setIsTransitioning(true);
      setDirection("prev");
      setTimeout(() => {
        setCurrentQuestion((prev) => prev - 1);
        setIsTransitioning(false);
      }, 400);
    }
  }, [currentQuestion, isTransitioning]);

  const handleStart = () => {
    setExit(true);
    setTimeout(() => {
      setScreen("quiz");
      setExit(false);
    }, 400);
  };

  const handleExit = () => {
    setExit(true);
    setTimeout(() => {
      navigate("/");
    }, 400);
  };

  const progress = ((currentQuestion) / questions.length) * 100;
  const question = questions[currentQuestion];

  // ==================== INTRO SCREEN ====================
  if (screen === "intro") {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col">
        {/* Minimal header */}
        <div className="px-6 py-4 flex items-center justify-between">
          <button
            onClick={handleExit}
            className="flex items-center gap-2 font-body text-sm text-secondary-500 hover:text-primary-500 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            Voltar
          </button>
          <div className="flex items-center gap-2">
            <i className="ri-leaf-line text-primary-500 text-lg"></i>
            <img src={assetUrl("/images/clinica-logo.png")} alt="Clínica de Ayurveda" className="w-32 sm:w-40" />
          </div>
          <div className="w-16"></div>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-8">
          <div className={`max-w-xl w-full text-center transition-all duration-500 ${exit ? "opacity-0 translate-y-4" : "opacity-100 translate-y-0"}`}>
            {/* Decorative circle */}
            <div className="w-20 h-20 rounded-full bg-primary-500/10 flex items-center justify-center mx-auto mb-8">
              <i className="ri-questionnaire-line text-primary-500 text-3xl"></i>
            </div>

            <span className="inline-block text-accent-500 font-body text-sm tracking-[0.2em] uppercase mb-4">
              Teste dos Doshas
            </span>
            <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4 leading-tight">
              Descubra seu perfil ayurvédico
            </h1>
            <p className="font-body text-secondary-500 text-lg mb-2 max-w-md mx-auto">
              81 perguntas sobre seus hábitos, corpo e mente
            </p>
            <p className="font-body text-secondary-500/60 text-sm mb-10">
              Duração aproximada: 10 minutos
            </p>

            <p className="text-xs text-secondary-500 mb-6">Teste dos doshas e análise completa por R$ 9,90. Pagamento único. Este teste não substitui consulta médica.</p>
            {/* Info cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10 max-w-lg mx-auto">
              <div className="bg-white rounded-xl p-4 border border-primary-500/5">
                <i className="ri-heart-pulse-line text-primary-500 text-xl mb-2"></i>
                <p className="font-body text-sm text-foreground-950 font-medium">Corpo</p>
                <p className="font-body text-xs text-secondary-500">Estrutura física</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-primary-500/5">
                <i className="ri-brain-line text-primary-500 text-xl mb-2"></i>
                <p className="font-body text-sm text-foreground-950 font-medium">Mente</p>
                <p className="font-body text-xs text-secondary-500">Padrões mentais</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-primary-500/5">
                <i className="ri-sun-line text-primary-500 text-xl mb-2"></i>
                <p className="font-body text-sm text-foreground-950 font-medium">Hábitos</p>
                <p className="font-body text-xs text-secondary-500">Rotina diária</p>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-white rounded-2xl p-6 mb-10 text-left max-w-lg mx-auto border border-primary-500/5">
              <h3 className="font-heading text-foreground-950 text-base mb-4">Como responder:</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 font-body text-sm text-secondary-500">
                  <div className="w-6 h-6 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-primary-500 text-xs"></i>
                  </div>
                  Responda com base em como você se sente geralmente
                </li>
                <li className="flex items-center gap-3 font-body text-sm text-secondary-500">
                  <div className="w-6 h-6 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-primary-500 text-xs"></i>
                  </div>
                  Não pense demais — sua primeira resposta é a mais natural
                </li>
                <li className="flex items-center gap-3 font-body text-sm text-secondary-500">
                  <div className="w-6 h-6 rounded-full bg-primary-500/10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-primary-500 text-xs"></i>
                  </div>
                  Seu progresso é salvo automaticamente
                </li>
              </ul>
            </div>

            <button
              onClick={handleStart}
              className="bg-primary-500 text-background-50 px-12 py-4 rounded-full font-body font-semibold text-base tracking-wide shadow-lg shadow-primary-500/20 hover:shadow-xl hover:shadow-primary-500/30 transition-all cursor-pointer whitespace-nowrap"
            >
              COMEÇAR TESTE
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== PROCESSING SCREEN ====================
  if (screen === "processing") {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          {/* Animated icon */}
          <div className="relative w-24 h-24 mx-auto mb-10">
            <div className="absolute inset-0 rounded-full bg-primary-500/10 animate-ping"></div>
            <div className="absolute inset-2 rounded-full bg-primary-500/20 animate-ping" style={{ animationDelay: "0.3s" }}></div>
            <div className="absolute inset-0 rounded-full bg-primary-500 flex items-center justify-center">
              <i className="ri-brain-line text-background-50 text-3xl"></i>
            </div>
          </div>

          <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 mb-3">
            Processando seu resultado
          </h2>
          <p className="font-body text-secondary-500 text-sm mb-10">
            Estamos analisando seus padrões de Vata, Pitta e Kapha
          </p>

          {/* Progress steps */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 ${processingStep >= 1 ? "bg-primary-500" : "bg-background-200"}`}>
                <i className={`ri-check-line text-sm ${processingStep >= 1 ? "text-background-50" : "text-secondary-500"}`}></i>
              </div>
              <div className="flex-1 text-left">
                <p className={`font-body text-sm transition-colors duration-500 ${processingStep >= 1 ? "text-foreground-950" : "text-secondary-500"}`}>
                  Analisando padrões de Vata
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 ${processingStep >= 2 ? "bg-primary-500" : "bg-background-200"}`}>
                <i className={`ri-check-line text-sm ${processingStep >= 2 ? "text-background-50" : "text-secondary-500"}`}></i>
              </div>
              <div className="flex-1 text-left">
                <p className={`font-body text-sm transition-colors duration-500 ${processingStep >= 2 ? "text-foreground-950" : "text-secondary-500"}`}>
                  Analisando padrões de Pitta
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 ${processingStep >= 3 ? "bg-primary-500" : "bg-background-200"}`}>
                <i className={`ri-check-line text-sm ${processingStep >= 3 ? "text-background-50" : "text-secondary-500"}`}></i>
              </div>
              <div className="flex-1 text-left">
                <p className={`font-body text-sm transition-colors duration-500 ${processingStep >= 3 ? "text-foreground-950" : "text-secondary-500"}`}>
                  Analisando padrões de Kapha
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 ${processingStep >= 4 ? "bg-primary-500" : "bg-background-200"}`}>
                <i className={`ri-check-line text-sm ${processingStep >= 4 ? "text-background-50" : "text-secondary-500"}`}></i>
              </div>
              <div className="flex-1 text-left">
                <p className={`font-body text-sm transition-colors duration-500 ${processingStep >= 4 ? "text-foreground-950" : "text-secondary-500"}`}>
                  Calculando percentuais
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-500 ${processingStep >= 5 ? "bg-primary-500" : "bg-background-200"}`}>
                <i className={`ri-check-line text-sm ${processingStep >= 5 ? "text-background-50" : "text-secondary-500"}`}></i>
              </div>
              <div className="flex-1 text-left">
                <p className={`font-body text-sm transition-colors duration-500 ${processingStep >= 5 ? "text-foreground-950" : "text-secondary-500"}`}>
                  Gerando seu relatório personalizado
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ==================== QUIZ SCREEN ====================
  return (
    <div className="min-h-screen bg-background-50 flex flex-col" ref={containerRef}>
      {/* Progress bar - thin line at top */}
      <div className="w-full h-1.5 bg-background-200/50" role="progressbar" aria-label="Progresso do questionário" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)}>
        <div
          className="h-full bg-primary-500 transition-all duration-500 ease-out"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Header */}
      <div className="px-6 py-4 flex items-center justify-between">
        <button
          onClick={handleBack}
          disabled={currentQuestion === 0 || isTransitioning}
          className={`flex items-center gap-2 font-body text-sm transition-colors cursor-pointer ${
            currentQuestion === 0
              ? "text-secondary-300 cursor-not-allowed"
              : "text-secondary-500 hover:text-primary-500"
          }`}
        >
          <i className="ri-arrow-left-line"></i>
          <span className="hidden sm:inline">Voltar</span>
        </button>

        <div className="flex items-center gap-2">
          <i className="ri-leaf-line text-primary-500 text-lg"></i>
          <img src={assetUrl("/images/clinica-logo.png")} alt="Clínica de Ayurveda" className="w-32 sm:w-40" />
        </div>

        <button
          onClick={handleExit}
          className="font-body text-sm text-secondary-500 hover:text-red-500 transition-colors cursor-pointer"
        >
          Salvar e sair
        </button>
      </div>

      {/* Main quiz area */}
      <div className="flex-1 flex items-center justify-center px-6 py-4 sm:py-8">
        <div className="w-full max-w-2xl">
          {/* Animated question container */}
          <div
            className={`transition-all duration-400 ease-in-out ${
              isTransitioning
                ? direction === "next"
                  ? "opacity-0 translate-x-12"
                  : "opacity-0 -translate-x-12"
                : exit
                  ? "opacity-0 translate-y-8"
                  : "opacity-100 translate-x-0"
            }`}
          >
            {/* Progress counter */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <span className="font-body text-xs text-accent-500 tracking-wider uppercase">
                  {question.dosha === "vata" && "Vata — Ar + Espaço"}
                  {question.dosha === "pitta" && "Pitta — Fogo + Água"}
                  {question.dosha === "kapha" && "Kapha — Terra + Água"}
                </span>
                <span className="font-body text-xs text-secondary-500">
                  {currentQuestion + 1} / {questions.length}
                </span>
              </div>
              {/* Progress dots */}
              <div className="flex items-center gap-1 mb-6">
                <div className="flex-1 h-1 bg-background-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary-500 rounded-full transition-all duration-500"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Question */}
            <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 leading-snug mb-8 md:mb-10">
              {question.text}
            </h2>

            {/* Answer buttons - large, clear, modern */}
            <div className="space-y-3">
              {answerOptions.map((option) => {
                const isSelected = answers[question.id] === option.value;
                return (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(option.value)}
                    disabled={isTransitioning}
                    aria-pressed={isSelected}
                    className={`w-full flex items-center gap-4 p-5 md:p-6 rounded-xl border-2 transition-all duration-300 cursor-pointer ${
                      isSelected
                        ? "border-primary-500 bg-primary-50 text-primary-700"
                        : "border-background-200/60 bg-white hover:border-primary-300 hover:bg-primary-50/30 text-foreground-950"
                    }`}
                  >
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 flex-shrink-0 ${
                        isSelected
                          ? "bg-primary-500 text-background-50"
                          : "bg-background-200/40 text-secondary-500"
                      }`}
                    >
                      <i className={option.icon}></i>
                    </div>
                    <span className="font-body text-base md:text-lg font-medium text-left">
                      {option.label}
                    </span>
                    {isSelected && (
                      <i className="ri-check-line text-primary-500 ml-auto text-xl flex-shrink-0"></i>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom info */}
      <div className="px-6 py-4 text-center border-t border-primary-500/5">
        <div className="flex items-center justify-center gap-2 mb-1">
          <span className="font-body text-xs text-secondary-500">
            {Math.round(progress)}% completo
          </span>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-1">
          {questions.map((_, i) => (
            <div
              key={i}
              className={`w-1 h-1 rounded-full transition-colors ${
                i < currentQuestion ? "bg-primary-500" : "bg-background-300"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
